import { collection, getDocs } from "firebase/firestore";
import { db } from '../../firebase.js';
import mapData from "../../assets/data/custom.geo.json";
import { formatCountry, fixCountryNameMap } from './formatCountry.js';

// Cache for storing country-year doc maps
const countryCache = {};

/**
 * Fetch the documents within "World -> {country} -> {year}" and build a map:
 *   docMap[docID] = docData
 */
const GetTableData = async (country, year) => {
    const cacheKey = `${country}_${year}`;
    if (countryCache[cacheKey]) {
        return countryCache[cacheKey];
    }

    const docMap = {};
    try {
        const querySnapshot = await getDocs(collection(db, "World", country, year));
        querySnapshot.forEach((docSnap) => {
            docMap[docSnap.id] = docSnap.data();
        });
        countryCache[cacheKey] = docMap; // Cache the result
    } catch (error) {
        console.error(`Error fetching data for ${country} in ${year}:`, error);
    }

    return docMap;
};

const filterCountries = (countries, country) => {
    const formattedCountries = formatCountry(country);
    const selectedCountrySet = new Set(formattedCountries.map(sc => sc.value));

    if (selectedCountrySet.has('World')) {
        return countries;
    }

    return countries.filter(c => selectedCountrySet.has(c.id));
};

// A small lookup so we know which doc ID to reference for each "indicator[1]"
const docNameMap = {
    // For standard usage
    "electricityData": "SummaryElectricity",
    "summaryData": "SummaryHC",
    "powerData": "PowerPlants",
    "hcData": "HCProjects",

    // For table usage
    "powerDataReport": "PowerPlants",
    "powerOperation": "PowerPlants",
    "powerTotal": "PowerPlants",
    "hcTotal": "HCProjects",
    "hcProjects": "HCProjects",
};

// Suppose we define an array for checking GEA-like codes:
const GEA_CODES = [
    "World", "OECD", "APAC", "EMEA", "LATAM", "NA",
    "Africa", "Americas", "Asia", "Europe", "Oceania"
];

function codeToDisplayName(code) {
    switch (code) {
        case "World":
            return "World";
        case "OECD":
            return "OECD - Organization For Economic Co-operation and Development";
        case "APAC":
            return "APAC - Asia Pacific";
        case "EMEA":
            return "EMEA - Europe, Middle East and Africa";
        case "LATAM":
            return "LATAM - Latin America";
        case "NA":
            return "NA - North America";
        case "Africa":
        case "Americas":
        case "Asia":
        case "Europe":
        case "Oceania":
            return code;
        default:
            return code;
    }
}

/**
 * Creates data for Tables
 */
export const createDataTable = async (indicator, country, year) => {
    const countries = await getData();
    const filteredCountries = filterCountries(countries, country);

    const tableData = await Promise.all(filteredCountries.map(async (nation) => {
        const docMap = await GetTableData(nation.id, year);

        if (docMap) {
            let entry = null; // We'll build arrays or objects as the logic says

            /********************************************************
             * 1) powerData
             ********************************************************/
            if (indicator[1] === "powerData" && !indicator[0]) {
                // docMap["PowerPlants"] -> .powerData
                const powerDoc = docMap["PowerPlants"];
                if (!powerDoc?.powerData) return null;

                entry = [];
                // Process all plant data
                powerDoc.powerData.forEach((plant) => {
                    entry.push({
                        'Country': nation.id,
                        'Geothermal Field name': plant['Geothermal Field name'],
                        'Field operator': plant['Field operator'],
                        'Wells in operation': plant['Wells in operation'],
                        'Depth of deepest production well (m)': plant['Depth of deepest production well (m)'],
                        'Reservoir type': plant['Reservoir type'],
                        'System type': plant['System type'],
                        'Plant name or number': plant['Plant name or number'],
                        'Plant operator': plant['Plant operator'],
                        'Combined Heat and Power (CHP)?': plant['Combined Heat and Power plant (CHP)?'],
                        'Co-production plant?': plant['Co-production plant?'],
                        'Percentage of energy produced from geothermal (%)': plant['Percentage of energy produced from geothermal (%)'],
                        'Type of unit': plant['Type of unit'],
                        'Year of commission': plant['Year of commission'],
                        'Status': plant['Status'],
                        'Turbine manufacturer': plant['Turbine manufacturer'],
                        'Installed Capacity (MW)': plant['Installed Capacity (MW)'],
                        'Gross Energy Produced (GEP) (GWh/year)': plant['Gross Energy Produced (GEP) (GWh/year)'],
                        'Net Energy Produced (NEP) (GWh/year)': plant['Net Energy Produced (NEP) (GWh/year)']
                    });
                });

                // Add summary entry if we have at least one plant
                if (entry.length) {
                    const summaryElectricityDoc = docMap["SummaryElectricity"];
                    const totalGross = summaryElectricityDoc?.electricityData?.[0]?.['Total Gross Electrical generation GWh/yr'];
                    const totalCap = summaryElectricityDoc?.electricityData?.[0]?.['Total Installed Capacity (MWe)'];

                    entry.push({
                        'type': 'summary',
                        'Country': nation.id,
                        'Total Gross Electrical generation GWh/yr': totalGross,
                        'Total Installed Capacity (MWe)': totalCap
                    });
                }
                return entry;

                /********************************************************
                 * 2) hcData
                 ********************************************************/
            } else if (indicator[1] === "hcData" && !indicator[0]) {
                const hcDoc = docMap["HCProjects"];
                if (!hcDoc?.hcData) return null;

                entry = [];
                // Process all H&C data
                hcDoc.hcData.forEach(plant => {
                    entry.push({
                        'Country': nation.id,
                        'Geothermal Field location': plant['Geothermal Field location'],
                        'Geothermal Field name': plant['Geothermal Field name'],
                        'Type of geothermal system': plant['Type of geothermal system'],
                        'Wells in operation': plant['Wells in operation'],
                        'Number of wells drilled over the last 3 years': plant['Number of wells drilled over the last 3 years'],
                        'Average TVD of wells under operation': plant['Average TVD of wells under operation'],
                        'Max. temperature of the fluid': plant['Max. temperature of the fluid'],
                        'Project name': plant['Project name'],
                        'Project operator': plant['Project operator'],
                        'Geothermal application': plant['Geothermal application'],
                        'Geothermal production technology': plant['Geothermal production technology'],
                        'Nominal Capacity (MWt)': plant['Nominal Capacity (MWt)'],
                        'Energy used (net) (TJ/year)': plant['Energy used (net) (TJ/year)'],
                        'Energy produced (gross) (TJ/year)': plant['Energy produced (gross) (TJ/year)'],
                        'Hybrid energy system?': plant['Hybrid energy system?'],
                        'Percentage of energy produced from geothermal': plant['Percentage of energy produced from geothermal'],
                        'Inlet temperature (°C)': plant['Inlet temperature (°C)'],
                        'Outlet temperature (°C)': plant['Outlet temperature (°C)'],
                        'No. of terminals': plant['No. of terminals'],
                        'Calculations methodology': plant['Calculations methodology']
                    });
                });

                if (entry.length) {
                    const summaryHCDoc = docMap["SummaryHC"];
                    const sd = summaryHCDoc?.summaryData?.[summaryHCDoc.summaryData.length - 1];
                    entry.push({
                        'type': 'summary',
                        'Country': nation.id,
                        'Total Energy produced (TJ/year)': sd?.['Total Energy produced (TJ/year)'],
                        'Total Energy used (TJ/year)': sd?.['Total Energy used (TJ/year)'],
                        'Total Installed Capacity (MWt)': sd?.['Total Installed Capacity (MWt)'],
                    });
                }
                return entry;

                /********************************************************
                 * 3) countries
                 ********************************************************/
            } else if (indicator[1] === "countries" && !indicator[0]) {
                // ...
                const summaryElecDoc = docMap["SummaryElectricity"];
                const summaryHCDoc = docMap["SummaryHC"];

                let countryEntry = { 'Country': nation.id };

                const powerProductionVal = summaryElecDoc?.electricityData?.[0]?.['Total Gross Electrical generation GWh/yr'];
                const powerCapacityVal = summaryElecDoc?.electricityData?.[0]?.['Total Installed Capacity (MWe)'];
                const hcProductionVal = summaryHCDoc?.summaryData?.[summaryHCDoc.summaryData.length - 1]?.['Total Energy used (TJ/year)'];
                const hcCapacityVal = summaryHCDoc?.summaryData?.[summaryHCDoc.summaryData.length - 1]?.['Total Installed Capacity (MWt)'];

                countryEntry['Total Gross Electrical generation GWh/yr'] = powerProductionVal;
                countryEntry['Total Electrical Installed Capacity (MWe)'] = powerCapacityVal;
                countryEntry['Total Energy used (TJ/year)'] = hcProductionVal;
                countryEntry['Total H&C Installed Capacity (MWt)'] = hcCapacityVal;

                return countryEntry;

                /********************************************************
                 * 4) power
                 ********************************************************/
            } else if (indicator[1] === "power" && !indicator[0]) {
                // ...
                const powerDoc = docMap["PowerPlants"];
                if (!powerDoc?.powerData) return null;

                let organisedData = [];
                powerDoc.powerData.forEach(plant => {
                    const typology = plant['Reservoir type'] || plant['Type of unit'] || 'Unknown';
                    const GEP = parseFloat(plant['Gross Energy Produced (GEP) (GWh/year)']) || 0;
                    const capacity = parseFloat(plant['Installed Capacity (MW)']) || 0;

                    const existing = organisedData.find(e => e.Country === nation.id && e.Typology === typology);
                    if (existing) {
                        existing['Total Annual Gross Electrical Generation (GWh/yr)'] += GEP;
                        existing['Total Installed Capacity (MW)'] += capacity;
                    } else {
                        organisedData.push({
                            'Country': nation.id,
                            'Typology': typology,
                            'Total Annual Gross Electrical Generation (GWh/yr)': GEP,
                            'Total Installed Capacity (MW)': capacity
                        });
                    }
                });
                return organisedData.length ? organisedData : null;

                /********************************************************
                 * 5) hc
                 ********************************************************/
            } else if (indicator[1] === "hc" && !indicator[0]) {
                // ...
                const hcDoc = docMap["HCProjects"];
                if (!hcDoc?.hcData) return null;

                let organisedData = [];
                hcDoc.hcData.forEach(project => {
                    const typology = project['Geothermal application'] || 'Unknown';
                    const usedEnergy = parseFloat(project['Energy used (net) (TJ/year)']) || 0;
                    const capacity = parseFloat(project['Nominal Capacity (MWt)']) || 0;

                    const existing = organisedData.find(e => e.Country === nation.id && e.Typology === typology);
                    if (existing) {
                        existing['Total Annual Energy used (TJ/year)'] += usedEnergy;
                        existing['Total Nominal Capacity (MWt)'] += capacity;
                    } else {
                        organisedData.push({
                            'Country': nation.id,
                            'Typology': typology,
                            'Total Annual Energy used (TJ/year)': usedEnergy,
                            'Total Nominal Capacity (MWt)': capacity
                        });
                    }
                });
                return organisedData.length ? organisedData : null;

                /********************************************************
                 * 6) powerDataReport
                 ********************************************************/
            } else if (indicator[1] === "powerDataReport" && !indicator[0]) {
                const powerDoc = docMap["PowerPlants"];
                if (!powerDoc?.powerData) return null;

                let organisedData = [];
                let totalGrossCount = 0, totalCapCount = 0, plantNumber = 0;

                powerDoc.powerData.forEach(plant => {
                    const plantName = plant['Plant name or number'] || null;
                    const operator = plant['Plant operator'] || null;
                    const GEP = parseFloat(plant['Gross Energy Produced (GEP) (GWh/year)']) || 0;
                    const capacity = parseFloat(plant['Installed Capacity (MW)']) || 0;
                    totalGrossCount += GEP;
                    totalCapCount += capacity;
                    plantNumber++;

                    const existing = organisedData.find(
                        e => e.Country === nation.id && e['Plant Name'] === plantName
                    );
                    if (existing) {
                        existing['Number of Units']++;
                        existing['Gross Electrical Generation (GWh/yr)'] += GEP;
                        existing['Nominal Capacity (MWt)'] += capacity;
                    } else {
                        organisedData.push({
                            'Country': nation.id,
                            'Plant Name': plantName,
                            'Plant Operator': operator,
                            'Number of Units': 1,
                            'Gross Electrical Generation (GWh/yr)': GEP,
                            'Nominal Capacity (MWt)': capacity
                        });
                    }
                });

                // Add summary entry
                if (organisedData.length) {
                    const summaryElecDoc = docMap["SummaryElectricity"];
                    const totalGross = summaryElecDoc?.electricityData?.[0]?.['Total Gross Electrical generation GWh/yr'];
                    const totalCap = summaryElecDoc?.electricityData?.[0]?.['Total Installed Capacity (MWe)'];

                    organisedData.push({
                        'type': 'summary',
                        'Country': nation.id,
                        'Grand Total Gross Electrical generation GWh/year': totalGross,
                        'Grand Total Installed Capacity (MWe)': totalCap,
                        'Total Nominal Capacity of reported plants': totalCapCount,
                        'Total Gross Electrical Generation of reported plants': totalGrossCount,
                        'Total number of reported plants': plantNumber
                    });
                }

                return organisedData.length ? organisedData : null;

                /********************************************************
                 * 7) powerOperation
                 ********************************************************/
            } else if (indicator[1] === "powerOperation" && !indicator[0]) {
                // ...
                const powerDoc = docMap["PowerPlants"];
                if (!powerDoc?.powerData) return null;

                let entryArray = [];
                // Only pick Operating + has a reservoir type
                powerDoc.powerData.forEach((plant) => {
                    if (
                        plant['Status']?.toLowerCase() === 'operating' &&
                        plant['Type of unit']
                    ) {
                        entryArray.push({
                            'Country': nation.id,
                            'Geothermal Field': plant['Geothermal Field name'],
                            'Plant name or number': plant['Plant name or number'],
                            'Unit': plant['Power unit name or number'],
                            'Technology': plant['Type of unit'],
                            'Field operator': plant['Field operator'],
                            'Plant operator': plant['Plant operator'],
                            'Installed Capacity (MW)': plant['Installed Capacity (MW)'],
                            'Gross Energy Produced (GEP) (GWh/year)': plant['Gross Energy Produced (GEP) (GWh/year)']
                        });
                    }
                });

                // Sort + Summaries
                entryArray.sort((a, b) => {
                    if (a['Country'] < b['Country']) return -1;
                    if (a['Country'] > b['Country']) return 1;
                    if (a['Geothermal Field'] < b['Geothermal Field']) return -1;
                    if (a['Geothermal Field'] > b['Geothermal Field']) return 1;
                    if (a['Technology'] < b['Technology']) return -1;
                    if (a['Technology'] > b['Technology']) return 1;
                    return 0;
                });

                const organizedEntries = [];
                let currentCountry = null;
                let currentGeothermalField = null;
                let currentReservoirType = null;

                let reservoirTypeTotalCapacity = 0;
                let reservoirTypeTotalGEP = 0;
                let reservoirTypeUnitCount = 0;

                let geothermalFieldTotalCapacity = 0;
                let geothermalFieldTotalGEP = 0;
                let geothermalFieldUnitCount = 0;

                let countryUnitCount = 0;

                const summaryElecDoc = docMap["SummaryElectricity"];
                const totalGross = summaryElecDoc?.electricityData?.[0]?.['Total Gross Electrical generation GWh/yr'];
                const totalCap = summaryElecDoc?.electricityData?.[0]?.['Total Installed Capacity (MWe)'];

                for (let i = 0; i < entryArray.length; i++) {
                    const e = entryArray[i];
                    const cntry = e['Country'];
                    const gf = e['Geothermal Field'];
                    const rt = e['Technology'];
                    const instCap = parseFloat(e['Installed Capacity (MW)']) || 0;
                    const grossProd = parseFloat(e['Gross Energy Produced (GEP) (GWh/year)']) || 0;

                    const isNewCountry = (currentCountry !== cntry);
                    const isNewGF = isNewCountry || (currentGeothermalField !== gf);
                    const isNewRT = isNewGF || (currentReservoirType !== rt);

                    // Summaries for reservoir + field + country
                    if (currentReservoirType !== null && isNewRT) {
                        organizedEntries.push({
                            type: 'reservoirTypeSummary',
                            'Country': currentCountry,
                            'Geothermal Field': currentGeothermalField,
                            'Technology': currentReservoirType,
                            'Total Installed Capacity (MW)': reservoirTypeTotalCapacity,
                            'Gross Energy Produced (GEP) (GWh/year)': reservoirTypeTotalGEP,
                            'Total number of units': reservoirTypeUnitCount,
                        });
                        reservoirTypeTotalCapacity = 0;
                        reservoirTypeTotalGEP = 0;
                        reservoirTypeUnitCount = 0;
                    }

                    if (currentGeothermalField !== null && isNewGF) {
                        organizedEntries.push({
                            type: 'geothermalFieldSummary',
                            'Country': currentCountry,
                            'Geothermal Field': currentGeothermalField,
                            'Total Installed Capacity (MW)': geothermalFieldTotalCapacity,
                            'Gross Energy Produced (GEP) (GWh/year)': geothermalFieldTotalGEP,
                            'Total number of units': geothermalFieldUnitCount,
                        });
                        geothermalFieldTotalCapacity = 0;
                        geothermalFieldTotalGEP = 0;
                        geothermalFieldUnitCount = 0;
                    }

                    if (currentCountry !== null && isNewCountry) {
                        organizedEntries.push({
                            type: 'countrySummary',
                            'Country': currentCountry,
                            'Total Gross Electrical generation GWh/yr': totalGross,
                            'Total Installed Capacity (MWe)': totalCap,
                            'Total number of units': countryUnitCount,
                        });
                        countryUnitCount = 0;
                    }

                    currentCountry = cntry;
                    currentGeothermalField = gf;
                    currentReservoirType = rt;

                    organizedEntries.push({ type: 'normal', ...e });

                    reservoirTypeTotalCapacity += instCap;
                    reservoirTypeTotalGEP += grossProd;
                    reservoirTypeUnitCount += 1;

                    geothermalFieldTotalCapacity += instCap;
                    geothermalFieldTotalGEP += grossProd;
                    geothermalFieldUnitCount += 1;

                    countryUnitCount += 1;
                }

                // After loop, push final summaries
                if (currentReservoirType !== null) {
                    organizedEntries.push({
                        type: 'reservoirTypeSummary',
                        'Country': currentCountry,
                        'Geothermal Field': currentGeothermalField,
                        'Technology': currentReservoirType,
                        'Total Installed Capacity (MW)': reservoirTypeTotalCapacity,
                        'Gross Energy Produced (GEP) (GWh/year)': reservoirTypeTotalGEP,
                        'Total number of units': reservoirTypeUnitCount,
                    });
                }
                if (currentGeothermalField !== null) {
                    organizedEntries.push({
                        type: 'geothermalFieldSummary',
                        'Country': currentCountry,
                        'Geothermal Field': currentGeothermalField,
                        'Total Installed Capacity (MW)': geothermalFieldTotalCapacity,
                        'Gross Energy Produced (GEP) (GWh/year)': geothermalFieldTotalGEP,
                        'Total number of units': geothermalFieldUnitCount,
                    });
                }
                if (currentCountry !== null) {
                    organizedEntries.push({
                        type: 'countrySummary',
                        'Country': currentCountry,
                        'Total Gross Electrical generation GWh/yr': totalGross,
                        'Total Installed Capacity (MWe)': totalCap,
                        'Total number of units': countryUnitCount,
                    });
                }

                return organizedEntries;

                /********************************************************
                 * 8) hcProjects
                 ********************************************************/
            } else if (indicator[1] === "hcProjects" && !indicator[0]) {
                // ...
                const hcDoc = docMap["HCProjects"];
                if (!hcDoc?.hcData) return null;

                let organisedData = [];
                let totalCapacity = 0;
                let totalEnergy = 0;

                hcDoc.hcData.forEach((project, index) => {
                    if (index !== hcDoc.hcData.length - 1) {
                        organisedData.push({
                            'Country': nation.id,
                            'Location': project['Geothermal Field location'],
                            'Project Name': project['Project Name'],
                            'Project Operator': project['Project Operator'],
                            'Geothermal Application': project['Geothermal application'],
                            'Technology': project['Geothermal production technology'],
                            'Nominal Capacity (MW)': project['Nominal Capacity (MWt)'],
                            'Energy Used (TJ/yr)': project['Energy used (net) (TJ/year)']
                        });
                        totalCapacity += parseFloat(project['Nominal Capacity (MWt)']) || 0;
                        totalEnergy += parseFloat(project['Energy used (net) (TJ/year)']) || 0;
                    }
                });

                if (organisedData.length) {
                    const summaryHCDoc = docMap["SummaryHC"];
                    const sd = summaryHCDoc?.summaryData?.[summaryHCDoc.summaryData.length - 1];
                    organisedData.push({
                        type: 'summary',
                        'Country': nation.id,
                        'Grand Total Nominal Capacity (MW)': sd?.['Total Installed Capacity (MWt)'],
                        'Grand Total Energy Used (TJ/yr)': sd?.['Total Energy used (TJ/year)'],
                        'Total Nominal Capacity of large projects (MW)': totalCapacity,
                        'Total Energy Used in large projects (TJ/yr)': totalEnergy,
                        'Total number of projects': organisedData.length,
                    });
                }

                return organisedData.length ? organisedData : null;

                /********************************************************
                 * 9) powerTotal
                 ********************************************************/
            } else if (indicator[1] === "powerTotal" && !indicator[0]) {
                // ...
                const powerDoc = docMap["PowerPlants"];
                if (!powerDoc?.powerData) return null;

                let organisedData = [];
                powerDoc.powerData.forEach(plant => {
                    const type = plant['Type of unit'] || null;
                    const GEP = parseFloat(plant['Gross Energy Produced (GEP) (GWh/year)']) || 0;
                    const capacity = parseFloat(plant['Installed Capacity (MW)']) || 0;

                    const existing = organisedData.find(e => e.Country === nation.id && e['Technology'] === type);
                    if (existing) {
                        existing['Gross Electrical Generation (GWh/yr)'] += GEP;
                        existing['Nominal Capacity (MWt)'] += capacity;
                    } else {
                        organisedData.push({
                            'Country': nation.id,
                            'Technology': type,
                            'Gross Electrical Generation (GWh/yr)': GEP,
                            'Nominal Capacity (MWt)': capacity
                        });
                    }
                });

                if (organisedData.length) {
                    const summaryElecDoc = docMap["SummaryElectricity"];
                    const totalGross = summaryElecDoc?.electricityData?.[0]?.['Total Gross Electrical generation GWh/yr'];
                    const totalCap = summaryElecDoc?.electricityData?.[0]?.['Total Installed Capacity (MWe)'];

                    organisedData.push({
                        'type': 'summary',
                        'Country': nation.id,
                        'Total Gross Electrical generation GWh/yr': totalGross,
                        'Total Installed Capacity (MWe)': totalCap
                    });
                }
                return organisedData.length ? organisedData : null;

                /********************************************************
                 * 10) hcTotal
                 ********************************************************/
            } else if (indicator[1] === "hcTotal" && !indicator[0]) {
                // ...
                const hcDoc = docMap["HCProjects"];
                if (!hcDoc?.hcData) return null;

                let organisedData = [];
                hcDoc.hcData.forEach(project => {
                    const type = project['Geothermal application'] || null;
                    const usedEnergy = parseFloat(project['Energy used (net) (TJ/year)']) || 0;
                    const capacity = parseFloat(project['Nominal Capacity (MWt)']) || 0;

                    const existing = organisedData.find(e => e.Country === nation.id && e['Application'] === type);
                    if (existing) {
                        existing['Total Energy used (TJ/year)'] += usedEnergy;
                        existing['Total Nominal Capacity (MWt)'] += capacity;
                    } else {
                        organisedData.push({
                            'Country': nation.id,
                            'Application': type,
                            'Total Energy used (TJ/year)': usedEnergy,
                            'Total Nominal Capacity (MWt)': capacity
                        });
                    }
                });

                if (organisedData.length) {
                    const summaryHCDoc = docMap["SummaryHC"];
                    const sd = summaryHCDoc?.summaryData?.[summaryHCDoc.summaryData.length - 1];
                    organisedData.push({
                        'type': 'summary',
                        'Country': nation.id,
                        'Grand Total Energy used (TJ/year)': sd?.['Total Energy used (TJ/year)'],
                        'Grand Total Nominal Capacity (MWt)': sd?.['Total Installed Capacity (MWt)']
                    });
                }
                return organisedData.length ? organisedData : null;
            }
        } // if docMap
        return null;
    }));

    // Flatten all entries from all countries
    const finalRows = tableData.flat().filter(Boolean);

    // ----------------------------------------------------------
    // If the first selected country is 'World' or a GEA code,
    //        produce a new final "countrySummary" row with the
    //        aggregated sums you want.
    // ----------------------------------------------------------
    const firstSelected = country?.[0]?.value;
    if (GEA_CODES.includes(firstSelected)) {
        // We want a big summary for World or a GEA region
        const displayName = codeToDisplayName(firstSelected);

        if (indicator[1] === "powerDataReport") {
            // Skip creating regional summary here - it will be handled by Table.js aggregation logic
            // This prevents duplicate regional summaries
        }

        else if (indicator[1] === "hcProjects") {
            // 2) Summation for "hcProjects"
            // We'll sum the final row fields:
            //  'Total Nominal Capacity (MW)'
            //  'Total Energy Used (TJ/yr)'
            //  'Total Nominal Capacity of large projects (MW)'
            //  'Total Energy Used by large projects (TJ/yr)'
            let sumNomCap = 0;
            let sumUsed = 0;
            let sumProjCap = 0;
            let sumProjUsed = 0;

            finalRows.forEach(r => {
                if (r['Total Nominal Capacity (MW)']) {
                    sumNomCap += parseFloat(r['Total Nominal Capacity (MW)']) || 0;
                }
                if (r['Total Energy Used (TJ/yr)']) {
                    sumUsed += parseFloat(r['Total Energy Used (TJ/yr)']) || 0;
                }
                if (r['Total Nominal Capacity of large projects (MW)']) {
                    sumProjCap += parseFloat(r['Total Nominal Capacity of large projects (MW)']) || 0;
                }
                if (r['Total Energy Used by large projects (TJ/yr)']) {
                    sumProjUsed += parseFloat(r['Total Energy Used by large projects (TJ/yr)']) || 0;
                }
            });

            finalRows.push({
                type: 'countrySummary',
                'Region': displayName,
                'Total Nominal Capacity (MW)': sumNomCap,
                'Total Energy Used (TJ/yr)': sumUsed,
                'Total Nominal Capacity of large projects (MW)': sumProjCap,
                'Total Energy Used by large projects (TJ/yr)': sumProjUsed,
            });
        }

        else if (indicator[1] === "powerTotal") {
            // 3) Summation for "powerTotal"
            // The aggregator uses 'Total Gross Electrical generation GWh/yr' + 'Total Installed Capacity (MWe)'
            let sumCap = 0;
            let sumGen = 0;

            finalRows.forEach(r => {
                if (r['Total Installed Capacity (MWe)']) {
                    sumCap += parseFloat(r['Total Installed Capacity (MWe)']) || 0;
                }
                if (r['Total Gross Electrical generation GWh/yr']) {
                    sumGen += parseFloat(r['Total Gross Electrical generation GWh/yr']) || 0;
                }
            });

            finalRows.push({
                type: 'countrySummary',
                'Region': displayName,
                'Total Installed Capacity (MWe)': sumCap,
                'Annual Gross Electrical Generation (GWh/yr)': sumGen
            });
        }

        else if (indicator[1] === "hcTotal") {
            // 4) Summation for "hcTotal"
            // The aggregator uses 'Total Annual Energy used (TJ/year)' + 'Total Nominal Capacity (MWt)'
            let sumEnergy = 0;
            let sumCapacity = 0;

            finalRows.forEach(r => {
                if (r['Total Annual Energy used (TJ/year)']) {
                    sumEnergy += parseFloat(r['Total Annual Energy used (TJ/year)']) || 0;
                }
                if (r['Total Nominal Capacity (MWt)']) {
                    sumCapacity += parseFloat(r['Total Nominal Capacity (MWt)']) || 0;
                }
            });

            finalRows.push({
                type: 'countrySummary',
                'Region': displayName,
                'Total Annual Energy used (TJ/year)': sumEnergy,
                'Total Nominal Capacity (MWt)': sumCapacity
            });
        }
    }

    return finalRows;
};

/**
 * Helper to parse the doc for chart creation
 */
const fetchDataForCountry = (nation, year, docMap, indicator) => {
    const chartEntry = { 'Country': nation.id, 'Year': year };

    // If we have a direct docName, see if docMap[docName] is available
    const docKey = docNameMap[indicator[1]]; // e.g. "SummaryElectricity" or "HCProjects"
    const doc = docMap[docKey];

    // If user is referencing e.g. "electricityData" or "summaryData"...
    if (indicator[1] === "electricityData" && doc?.electricityData?.[0]) {
        const data = doc.electricityData[0];
        if (indicator[0] === 'Geothermal Gross Electrical generation GWh/yr') {
            if (parseFloat(data[indicator[0]]) !== 0) {
                chartEntry['Geothermal Gross Electrical generation GWh/yr'] = parseFloat(data[indicator[0]]);
            }
        } else if (indicator[0] === 'Geothermal Installed Capacity (MWe)') {
            if (parseFloat(data[indicator[0]]) !== 0) {
                chartEntry['Geothermal Installed Capacity (MWe)'] = parseFloat(data[indicator[0]]);
            }
        }
        // If there's other sub-conditions, they'd go here

    } else if (indicator[1] === "summaryData" && doc?.summaryData?.length > 0) {
        const sd = doc.summaryData[doc.summaryData.length - 1];
        if (indicator[0] === 'Total Energy used (TJ/year)') {
            if (parseFloat(sd[indicator[0]]) !== 0) {
                chartEntry['Total Energy used (TJ/year)'] = parseFloat(sd[indicator[0]]);
            }
        } else if (indicator[0] === 'Total Installed Capacity (MWt)') {
            if (parseFloat(sd[indicator[0]]) !== 0) {
                chartEntry['Total Installed Capacity (MWt)'] = parseFloat(sd[indicator[0]]);
            }
        } else if (!indicator[0]) {
            // i.e. if no indicator label, copy both
            Object.assign(chartEntry, {
                'Total Energy used (TJ/year)': parseFloat(sd["Total Energy used (TJ/year)"]) || 0,
                'Total Installed Capacity (MWt)': parseFloat(sd["Total Installed Capacity (MWt)"]) || 0,
            });
        }

    } else if (indicator[1] === "powerData" &&
        (indicator[0] === 'Installed Capacity (MW)' || indicator[0] === 'Gross Energy Produced (GEP) (GWh/year)')) {
        if (!doc?.powerData) return null;
        const dataArray = doc.powerData;
        const typologyField = 'Type of unit';
        const valueField = indicator[0];
        let typologySums = {};

        dataArray.forEach(plant => {
            let typology = plant[typologyField] || 'Unknown';
            let value = parseFloat(plant[valueField]) || 0;
            if (value !== 0) {
                typologySums[typology] = (typologySums[typology] || 0) + value;
            }
        });

        if (Object.keys(typologySums).length > 0) {
            Object.assign(chartEntry, typologySums);
            return chartEntry;
        } else {
            return null;
        }

    } else if (indicator[1] === "hcData" &&
        (indicator[0] === 'Nominal Capacity (MWt)' || indicator[0] === 'Energy used (net) (TJ/year)')) {
        if (!doc?.hcData) return null;
        const dataArray = doc.hcData;
        const typologyField = 'Geothermal application';
        const valueField = indicator[0];
        let typologySums = {};

        dataArray.forEach(plant => {
            let typology = plant[typologyField] || 'Unknown';
            let value = parseFloat(plant[valueField]) || 0;
            if (value !== 0) {
                typologySums[typology] = (typologySums[typology] || 0) + value;
            }
        });

        if (Object.keys(typologySums).length > 0) {
            Object.assign(chartEntry, typologySums);
            return chartEntry;
        } else {
            return null;
        }
    }

    // If the doc has valid data
    return (Object.keys(chartEntry).length > 2) ? chartEntry : null;
};

/**
 * Creates data for Charts
 */
export const createDataChart = async (indicator, country, year) => {
    const countries = await getData();
    const filteredCountries = filterCountries(countries, country);

    const chartData = await Promise.all(filteredCountries.map(async (nation) => {
        const docMap = await GetTableData(nation.id, year);
        if (!docMap) return null;

        const entry = fetchDataForCountry(nation, year, docMap, indicator);
        return entry;
    }));

    return chartData.filter(Boolean);
};

/**
 * Creates data for Map
 */
export const createDataMap = async (indicator, country, year) => {
    const countries = await getData();
    const filteredCountries = filterCountries(countries, country);
    const baseMapData = mapData;

    // Build a set of fixed country names for those that are selected.
    const selectedNamesSet = new Set(filteredCountries.map(nation => fixCountryNameMap(nation.id)));

    // We'll update baseMapData.features for each relevant country
    await Promise.all(filteredCountries.map(async (nation) => {
        const docMap = await GetTableData(nation.id, year);
        if (!docMap) return;

        let electricityData;
        let summaryData;

        // If combinedProduction or combinedCapacity, we forcibly pull from SummaryElectricity + SummaryHC
        if (indicator[1] === 'combinedProduction' || indicator[1] === 'combinedCapacity') {
            electricityData = docMap["SummaryElectricity"]?.electricityData;
            summaryData = docMap["SummaryHC"]?.summaryData;
        } else {
            // Otherwise, pick the doc from docNameMap
            const docKey = docNameMap[indicator[1]];
            const doc = docMap[docKey];
            electricityData = doc?.electricityData;
            summaryData = doc?.summaryData;
        }

        const fixedName = fixCountryNameMap(nation.id);

        baseMapData.features = baseMapData.features.map(feature => {
            if (fixedName === feature.properties.name) {
                //General Link for source
                if (docMap?.['PowerProceeding']?.['Link']) {
                    feature.properties.link = docMap['PowerProceeding']['Link'];
                }
                // If we're referencing electricityData
                if (electricityData?.length > 0 && indicator[1] !== 'combinedProduction' && indicator[1] !== 'combinedCapacity') {
                    feature.properties.geothermal = parseFloat(electricityData[0][indicator[0]]);
                    feature.properties.geothermalText = indicator[0];
                    feature.properties.geothermal2 = false;
                    feature.properties.geothermalText2 = false;
                    feature.properties.combined = false;
                }

                // If referencing summaryData
                if (summaryData?.length > 0 && indicator[1] !== 'combinedProduction' && indicator[1] !== 'combinedCapacity') {
                    const latest = summaryData[summaryData.length - 1];
                    feature.properties.geothermal = parseFloat(latest[indicator[0]]);
                    feature.properties.geothermalText = indicator[0];
                    feature.properties.geothermal2 = false;
                    feature.properties.geothermalText2 = false;
                    feature.properties.combined = false;
                }

                // Handle combinedCapacity
                if (indicator[1] === 'combinedCapacity') {
                    let geoCap = parseFloat(docMap["SummaryElectricity"]?.electricityData?.[0]?.['Geothermal Installed Capacity (MWe)']) || NaN;
                    let hcCap = parseFloat(docMap["SummaryHC"]?.summaryData?.[docMap["SummaryHC"]?.summaryData.length - 1]?.['Total Installed Capacity (MWt)']) || NaN;

                    feature.properties.geothermal = geoCap;
                    feature.properties.geothermalText = 'Geothermal Installed Capacity (MWe)';
                    feature.properties.geothermal2 = hcCap;
                    feature.properties.geothermalText2 = 'Total Installed Capacity (MWt)';

                    feature.properties.combined = (!isNaN(geoCap) && !isNaN(hcCap));
                }

                // Handle combinedProduction
                if (indicator[1] === 'combinedProduction') {
                    let geoProd = parseFloat(docMap["SummaryElectricity"]?.electricityData?.[0]?.['Geothermal Gross Electrical generation GWh/yr']) || NaN;
                    let hcProd = parseFloat(docMap["SummaryHC"]?.summaryData?.[docMap["SummaryHC"]?.summaryData.length - 1]?.['Total Energy used (TJ/year)']) || NaN;

                    feature.properties.geothermal = geoProd;
                    feature.properties.geothermalText = 'Geothermal Gross Electrical generation GWh/yr';
                    feature.properties.geothermal2 = hcProd;
                    feature.properties.geothermalText2 = 'Total Energy produced (TJ/year)';

                    feature.properties.combined = (!isNaN(geoProd) && !isNaN(hcProd));
                }
            }
            return feature;
        });
    }));

    //Filter out contries not in the region
    baseMapData.features = baseMapData.features.map(feature => {
        if (!selectedNamesSet.has(feature.properties.name)) {
            feature.properties.geothermal = null;
            feature.properties.geothermalText = "";
            feature.properties.geothermal2 = null;
            feature.properties.geothermalText2 = "";
            feature.properties.combined = false;
        }
        return feature;
    });

    return baseMapData;
};

// Keep the same global/cachedYears logic
let cachedYears = null;

/**
 * Check which years exist for the first available country
 */
const getAvailableYears = async (db, countryList) => {
    if (cachedYears) {
        return cachedYears;
    }

    const possibleYears = [];
    for (let year = 1990; year <= 2024; year++) {
        possibleYears.push(year.toString());
    }

    for (const country of countryList) {
        const yearsWithData = ["1995", "2000", "2005", "2010", "2015", "2021", "2023"];
        /*for (const year of possibleYears) {
            const yearCollectionRef = collection(db, "World", country, year);
            try {
                const querySnapshot = await getDocs(yearCollectionRef);
                if (!querySnapshot.empty) {
                    yearsWithData.push(year);
                }
            } catch (error) {
                console.log(`Error fetching data for ${year}:`, error);
            }
        }*/
        if (yearsWithData.length > 0) {
            cachedYears = yearsWithData;
            return yearsWithData;
        }
    }

    return [];
};

export const getYears = async (countries) => {
    let countryList = countries.map(country => country.id);
    if (countryList.length) {
        try {
            const years = await getAvailableYears(db, countryList);
            return years;
        } catch (error) {
            console.error("Error fetching years:", error);
            return [];
        }
    }
    return [];
};

export const getData = async () => {
    const datas = [];
    try {
        const querySnapshot = await getDocs(collection(db, "World"));
        querySnapshot.forEach(data => {
            datas.push({
                id: data.id,
                ...data.data(),
            });
        });
    } catch (error) {
        console.error("Error fetching data from Firestore:", error);
    }
    return datas;
};